import { useState } from "react";
import { useAuditTrail } from "../hooks/useAuditTrail";
import "./AuditTrailPanel.css";

interface AuditTrailPanelProps {
  documentId: string;
  onClose: () => void;
}

/**
 * E-055 follow-on — group the raw action vocabulary into a small set of
 * human filter categories so a long history isn't a wall of rows.
 * Order matters: overrides are checked before fields so `field.overridden`
 * lands under Overrides.
 */
const FILTERS = [
  { key: "all", label: "All" },
  { key: "steps", label: "Steps" },
  { key: "fields", label: "Fields" },
  { key: "document", label: "Document" },
  { key: "overrides", label: "Overrides" },
] as const;

type FilterKey = (typeof FILTERS)[number]["key"];

function matchesFilter(action: string, filter: FilterKey): boolean {
  if (filter === "all") return true;
  const a = action.toLowerCase();
  if (a.includes("overrid")) return filter === "overrides";
  if (a.startsWith("field") || a.startsWith("line_item")) return filter === "fields";
  if (a.startsWith("step") || a.startsWith("workflow")) return filter === "steps";
  if (a.startsWith("document") || a.startsWith("page")) return filter === "document";
  return false; // uncategorized actions only appear under All
}

function getActionBadgeClass(action: string): string {
  const a = action.toLowerCase();
  if (a.includes("accept") || a.includes("complete") || a.includes("export")) return "at-badge--success";
  if (a.includes("reject") || a.includes("fail") || a.includes("error")) return "at-badge--danger";
  if (a.includes("hold") || a.includes("escalat")) return "at-badge--warning";
  return "at-badge--default";
}

function formatTimestamp(iso: string): string {
  const d = new Date(iso);
  return d.toLocaleString(undefined, {
    month: "short",
    day: "numeric",
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
  });
}

export function AuditTrailPanel({ documentId, onClose }: AuditTrailPanelProps) {
  const { entries, isLoading, error } = useAuditTrail(documentId);
  const [filter, setFilter] = useState<FilterKey>("all");

  const visibleEntries = entries.filter((e) => matchesFilter(e.action, filter));
  const activeLabel = FILTERS.find((f) => f.key === filter)?.label ?? "All";

  return (
    <div className="at-panel" data-testid="audit-trail-panel">
      <div className="at-header">
        <span className="at-title">AUDIT TRAIL</span>
        <button className="at-close" onClick={onClose} aria-label="Close audit trail">&times;</button>
      </div>

      {!isLoading && !error && entries.length > 0 && (
        <div className="at-filters" role="group" aria-label="Filter audit entries by action">
          {FILTERS.map((f) => (
            <button
              key={f.key}
              className={`at-filter${filter === f.key ? " at-filter--active" : ""}`}
              aria-pressed={filter === f.key}
              onClick={() => setFilter(f.key)}
            >
              {f.label}
            </button>
          ))}
        </div>
      )}

      <div className="at-body">
        {isLoading && (
          <div className="at-loading"><div className="loading-spinner" /></div>
        )}

        {error && <div className="at-error">{error}</div>}

        {!isLoading && !error && entries.length === 0 && (
          <div className="at-empty">No audit entries for this document.</div>
        )}

        {!isLoading && entries.length > 0 && visibleEntries.length === 0 && (
          <div className="at-empty">No {activeLabel} entries for this document.</div>
        )}

        {!isLoading && visibleEntries.length > 0 && (
          <div className="at-timeline">
            {visibleEntries.map((entry) => (
              <div key={entry.id} className="at-entry">
                <div className="at-dot" />
                <div className="at-content">
                  <div className="at-entry-header">
                    <span className={`at-badge ${getActionBadgeClass(entry.action)}`}>
                      {entry.action.replace(/_/g, " ")}
                    </span>
                    <span className="at-time">{formatTimestamp(entry.createdAt)}</span>
                  </div>
                  {entry.userName && (
                    <div className="at-user">{entry.userName}</div>
                  )}
                  {entry.details && (
                    <div className="at-details">{entry.details}</div>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
